/* Inline formatting inside a prose field.

   The block model has always allowed three things inside body text — `**bold**`,
   `` `code` `` and `[label](/route)` — and 465 bold runs across the material
   depend on it. Rendered without this, the asterisks print, which reads as a
   typo rather than as emphasis.

   React escapes everything it renders, so nothing here can put markup on the
   page; a link's destination is checked besides, because content is data.

   OWNED BY ITEM 3 OF docs/plan/00-prestavba-2026-08.md; written here by item 4 because the material page
   was showing raw asterisks. */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";

const PATTERN = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
/* The same, plus single-asterisk italics — and only for the model's
   commentary; see blocks/markdown.tsx for why the material does not get them.
   Bold is first in the alternation, so `**x**` is never read as an empty
   italic. The italic itself demands a non-space after the opening asterisk,
   which is what keeps 3 * 4 a multiplication. */
const PATTERN_EM = /(\*\*[^*]+\*\*|\*[^\s*][^*\n]*?\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
const LINK = /^\[([^\]]+)\]\(([^)]+)\)$/;

export function inline(text: string, hrefOf: (path: string) => string,
                       italics = false): ReactNode[] {
  return text.split(italics ? PATTERN_EM : PATTERN).filter(Boolean).map((piece, i) => {
    if (piece.startsWith("**") && piece.endsWith("**")) {
      return <strong key={i}>{piece.slice(2, -2)}</strong>;
    }
    if (italics && piece.length > 2 && piece.startsWith("*") && piece.endsWith("*")) {
      return <em key={i}>{piece.slice(1, -1)}</em>;
    }
    if (piece.startsWith("`") && piece.endsWith("`") && piece.length > 1) {
      return <code key={i}>{piece.slice(1, -1)}</code>;
    }
    const link = LINK.exec(piece);
    if (link) {
      const [, label, target] = link as unknown as [string, string, string];
      /* Inside the portal only. Content is data, and a relative destination is
         the one thing a document is allowed to point at. */
      if (target.startsWith("/")) {
        return <Link key={i} to={hrefOf(target)}>{label}</Link>;
      }
      return <span key={i}>{label}</span>;
    }
    return <span key={i}>{piece}</span>;
  });
}

/* Paragraphs, split on a blank line. Prose fields in the material carry them. */
export function paragraphs(text: string, hrefOf: (path: string) => string): ReactNode[] {
  return text.split(/\n{2,}/).map((para, i) => <p key={i}>{inline(para, hrefOf)}</p>);
}
